import { Component, OnInit } from '@angular/core';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { ActivatedRoute, Router } from '@angular/router';
import { GoogleTagManagerService } from '@app-core/services/google-tag-manager/google-tag-manager.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { tabQueryParamNames } from '@app-assets/constants/assets.constants';
import { DataService } from '@app-core/services/data/data.service';
import { KEYBOARD_SHORTCUTS } from '@app-core/constants/keyboard-shortcuts.constants';
import { KeyboardShortcutsService } from '@app-core/services/keyboard-shortcuts/keyboard-shortcuts.service';
import { MixPanelService } from '@app-core/services/mixpanel/mix-panel.service';

const subTabNameIndexMapping = {
  cameraevent: 0,
  healthevent: 1,
  cameraonlinestatus: 2,
};

@Component({
  selector: 'app-diagnostics',
  templateUrl: './fleet-diagnostics.component.html',
  styleUrls: ['./fleet-diagnostics.component.scss'],
})
export class FleetDiagnosticsComponent implements OnInit {
  public currentSubTabIndex: number = 0;
  private ngUnsubscribe: Subject<void> = new Subject<void>();
  private routeQueryParams: any;
  public currentSubTab: string = '';
  public currentTheme: string = 'light';
  public currentFleet: string = '';
  public keyboardShortcuts = KEYBOARD_SHORTCUTS;
  public currentOS = 'windows';

  constructor(
    public dataService: DataService,
    private router: Router,
    private route: ActivatedRoute,
    private gtmService: GoogleTagManagerService,
    private keyboardShortcutsService: KeyboardShortcutsService,
    private mixpanelService: MixPanelService
  ) {}

  ngOnInit(): void {
    this.dataService._currentTheme.pipe(takeUntil(this.ngUnsubscribe)).subscribe((value: string) => {
      if (value) {
        this.currentTheme = value;
      }
    });

    this.dataService._currentFleet.pipe(takeUntil(this.ngUnsubscribe)).subscribe((value: string) => {
      if (value) {
        this.currentFleet = value;
      }
    });
    this.route.queryParams.pipe(takeUntil(this.ngUnsubscribe)).subscribe((params: any) => {
      this.routeQueryParams = params;
      const { subTab = '', tab = '' } = params || {};
      this.currentSubTab = subTab;
      if (tab === 'diagnostics') {
        this.currentSubTabIndex = subTabNameIndexMapping[subTab] || 0;
      }
      const validSubTab = (tabQueryParamNames[tab] || []).includes(subTab);
      if (!validSubTab) {
        this.setDefaultQueryParams();
      }
      switch (this.currentSubTabIndex) {
        case 0:
          this.gtmService.customTabs('/health-check', 'Camera Event', 'cameraevent');
          break;
        case 1:
          this.gtmService.customTabs('/health-check', 'Health Event', 'healthevent');
          break;
        default:
          this.gtmService.customTabs('/health-check', 'Camera Online Status', 'cameraonlinestatus');
          break;
      }
    });
    this.configureKeyboardShortcuts();
  }

  public configureKeyboardShortcuts() {
    this.keyboardShortcutsService
      .addShortcut({ keys: this.keyboardShortcuts.goToTab4[this.currentOS] })
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        this.onKeyboardTabChange(0);
      });
    this.keyboardShortcutsService
      .addShortcut({ keys: this.keyboardShortcuts.goToTab5[this.currentOS] })
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        this.onKeyboardTabChange(1);
      });
    this.keyboardShortcutsService
      .addShortcut({ keys: this.keyboardShortcuts.goToTab6[this.currentOS] })
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        this.onKeyboardTabChange(2);
      });
  }

  public onKeyboardTabChange(tabIndex: number) {
    const [subTabName = 'diagnostic'] = Object.entries(subTabNameIndexMapping).find(([, index]) => index === tabIndex);
    this.router.navigate([], {
      queryParams: {
        ...this.routeQueryParams,
        deviceId: null,
        eventType: null,
        subTab: subTabName,
      },
    });
  }

  public onPageSubTabChange(event: MatTabChangeEvent) {
    const [subTabName = ''] = Object.entries(subTabNameIndexMapping).find(([, index]) => index === event.index);

    if (this.routeQueryParams.subTab !== subTabName) {
      this.router.navigate([], {
        queryParams: {
          ...this.routeQueryParams,
          deviceId: null,
          eventType: null,
          subTab: subTabName,
        },
      });
    }

    let displayTabName = '';
    if (subTabName === 'cameraevent') {
      displayTabName = 'Camera Visibility Events Tab clicked - Diagnostics Tab / Assets Page';
    } else if (subTabName === 'healthevent') {
      displayTabName = 'Device Diagnostic Events Tab Clicked - Diagnostics Tab / Assets Page';
    } else {
      displayTabName = 'Device Online Status Tab Clicked - Diagnostics Tab / Assets Page';
    }
    this.mixpanelService.track(`${displayTabName}`, {
      'Tab Name': subTabName,
    });
  }

  private setDefaultQueryParams() {
    setTimeout(() => {
      this.router.navigate([], {
        queryParams: {
          ...this.routeQueryParams,
        },
        replaceUrl: true,
      });
    }, 0);
  }
}
